import React, { useEffect, useLayoutEffect, useRef, useCallback } from 'react';

const words = [
  'WHY',
  'THE CAUSE',
  'THE REASON',
  'THE UNCERTAINTY',
  'THE OUTCOME',
  'THE CONSEQUENCES',
  'HOW TO INTERVENE',
];

const N             = words.length;
const ANGLE_STEP    = (2 * Math.PI) / N;   // angular slice per word
const RADIUS        = 115;                  // cylinder radius in px
const MS_PER_WORD   = 2600;                 // ms each word takes to travel one slot
const OMEGA         = ANGLE_STEP / MS_PER_WORD;  // constant angular velocity (rad/ms)

// Place word i on the drum at the given drum angle — pure sin/cos, no easing
function placeWord(el, drumAngle, i) {
  const a    = drumAngle - i * ANGLE_STEP;
  const sinA = Math.sin(a);
  const cosA = Math.cos(a);
  const y     = sinA * RADIUS;
  const scale = 0.68 + 0.32 * ((cosA + 1) / 2);
  const alpha = Math.max(0, (cosA + 0.30) / 1.30);
  el.style.transform = `translateY(calc(-50% + ${y.toFixed(2)}px)) scale(${scale.toFixed(4)})`;
  el.style.opacity   = alpha.toFixed(4);
  // Only write zIndex when it actually changes (avoids triggering style recalc)
  const z = String(Math.round((cosA + 1) * 5 + 1));
  if (el.dataset.z !== z) { el.style.zIndex = z; el.dataset.z = z; }
}

function applyGradient(els, idx) {
  els.forEach((el, i) => {
    if (!el) return;
    if (i === idx) {
      el.style.background           = 'linear-gradient(135deg, #3B82F6 0%, #818CF8 100%)';
      el.style.webkitBackgroundClip = 'text';
      el.style.webkitTextFillColor  = 'transparent';
      el.style.backgroundClip       = 'text';
    } else {
      el.style.background           = 'none';
      el.style.webkitBackgroundClip = 'unset';
      el.style.webkitTextFillColor  = '#F8FAFC';
      el.style.backgroundClip       = 'unset';
    }
  });
}

const WordCarousel = () => {
  const wordRefs  = useRef([]);
  const startTime = useRef(null);   // timestamp of first RAF frame
  const activeIdx = useRef(0);
  const rafRef    = useRef(null);

  const applyAll = useCallback((angle) => {
    wordRefs.current.forEach((el, i) => { if (el) placeWord(el, angle, i); });
  }, []);

  // Set correct positions before first paint — no flash
  useLayoutEffect(() => {
    applyAll(0);
    applyGradient(wordRefs.current, 0);
  }, [applyAll]);

  // Single RAF loop — constant angular velocity, no steps, no pauses
  useEffect(() => {
    const loop = (ts) => {
      if (startTime.current === null) startTime.current = ts;
      const angle = (ts - startTime.current) * OMEGA;
      applyAll(angle);

      // Switch gradient when the nearest word changes
      const nearest = ((Math.round(angle / ANGLE_STEP) % N) + N) % N;
      if (nearest !== activeIdx.current) {
        activeIdx.current = nearest;
        applyGradient(wordRefs.current, nearest);
      }

      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [applyAll]);

  return (
    <div
      className="relative w-full select-none"
      style={{
        height:          '280px',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 16%, black 84%, transparent 100%)',
        maskImage:       'linear-gradient(to bottom, transparent 0%, black 16%, black 84%, transparent 100%)',
      }}
    >
      {words.map((word, i) => (
        <div
          key={word}
          ref={el => { wordRefs.current[i] = el; }}
          className="absolute left-0 right-0 top-1/2 text-center font-bold tracking-tight whitespace-nowrap px-4"
          style={{
            fontSize:   'clamp(1.4rem, 3.5vw, 2.8rem)',
            willChange: 'transform, opacity',
          }}
        >
          {word}
        </div>
      ))}
    </div>
  );
};

export default WordCarousel;
